// frontend/Blog/BlogPostPage.jsx
// Individual blog post reader — requires login (ProtectedRoute).
// Route: /blog/:slug
// Fetches from: GET /api/blogs/:slug

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getBlogBySlug } from "../../services/publicblogapi";
import logo from "../../img/image.png";
import {
  ArrowLeft, Clock, Eye, Calendar, Tag as TagIcon,
  Share2, Copy, Check,
  ChevronRight, Loader2, BookOpen, TrendingUp,
  Monitor, BookMarked, AlignJustify, X
} from "lucide-react";

// ─── Utilities ────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
};

const categoryColor = (cat) => {
  const p = {
    Education: "#4361ee", Technology: "#7c3aed", Career: "#f4803e",
    University: "#06b6d4", "Entrance Exams": "#ef4444", Science: "#22c55e",
    "Arts & Humanities": "#ec4899", Health: "#16a34a", Business: "#f59e0b",
    "Study Abroad": "#8b5cf6",
  };
  return p[cat] || "#6b7280";
};

// ─── Global Styles ────────────────────────────────────────────────────────────
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Lora:ital,wght@0,400;0,600;0,700;1,400;1,500&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideUp { from { transform:translateY(100%); } to { transform:translateY(0); } }
  @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }

  /* ── Copy Protection ── */
  .no-select {
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
    -webkit-touch-callout: none !important;
  }

  /* ── Post Content ── */
  .post-content { overflow-wrap: break-word; word-break: break-word; }
  .post-content h1,.post-content h2 { font-family:'Playfair Display',serif; }
  .post-content h1 { font-size:clamp(1.4em,4vw,2em); font-weight:700; margin:1.5rem 0 .6rem; color:#1a1a2e; }
  .post-content h2 { font-size:clamp(1.15em,3vw,1.55em); font-weight:600; margin:1.4rem 0 .5rem; color:#1a1a2e; }
  .post-content h3 { font-size:clamp(1.05em,2.5vw,1.25em); font-weight:600; margin:1.1rem 0 .4rem; color:#1a1a2e; }
  .post-content h4 { font-size:1.05em; font-weight:600; margin:.8rem 0 .3rem; color:#374151; }
  .post-content p  { margin:0 0 1.1em; }
  .post-content blockquote {
    border-left:4px solid #f4803e; margin:1.8rem 0;
    padding:14px clamp(14px,3vw,22px);
    background:#fff8f4; border-radius:0 10px 10px 0;
    font-style:italic; color:#6b7280;
  }
  .post-content ul { list-style:disc;    padding-left:clamp(1.2em,4vw,2em); margin:.6em 0 1.1em; }
  .post-content ol { list-style:decimal; padding-left:clamp(1.2em,4vw,2em); margin:.6em 0 1.1em; }
  .post-content li { margin-bottom:.4em; line-height:1.7; }
  .post-content a  { color:#4361ee; text-decoration:underline; word-break:break-all; }
  .post-content a:hover { color:#f4803e; }
  .post-content code {
    background:#f0f2f8; color:#e63946;
    padding:2px 6px; border-radius:4px;
    font-family:'Courier New',monospace; font-size:.88em;
  }
  .post-content pre {
    background:#1a1a2e; color:#a8d8a8;
    padding:clamp(14px,3vw,22px); border-radius:12px;
    font-family:'Courier New',monospace;
    overflow-x:auto; -webkit-overflow-scrolling:touch;
    margin:1.8rem 0; line-height:1.65; font-size:14px;
  }
  .post-content table {
    width:100%; border-collapse:collapse; margin:1.5rem 0;
    font-size:clamp(12px,1.5vw,14px);
    display:block; overflow-x:auto; -webkit-overflow-scrolling:touch;
  }
  .post-content td,.post-content th {
    padding:clamp(8px,1.5vw,10px) clamp(10px,2vw,14px);
    border:1px solid #e5e7eb; white-space:nowrap;
  }
  .post-content th { background:#1a1a2e; color:#fff; font-weight:600; }
  .post-content img { max-width:100%; height:auto; border-radius:12px; display:block; }
  .post-content hr  { border:none; border-top:2px solid #e5e7eb; margin:2rem 0; }
  .post-content figure { margin:1.8rem 0; max-width:100%; }
  .post-content figcaption { text-align:center; font-size:13px; color:#9ca3af; font-style:italic; margin-top:8px; }
  .post-content iframe,.post-content video,.post-content embed { max-width:100%; }

  /* ── Reading Content ── */
  .reading-content { overflow-wrap:break-word; word-break:break-word; }
  .reading-content h1,.reading-content h2,.reading-content h3 { font-family:'Playfair Display',Georgia,serif; }
  .reading-content h1 { font-size:1.8em; font-weight:700; margin:2rem 0 .7rem; }
  .reading-content h2 { font-size:1.45em; font-weight:600; margin:1.8rem 0 .6rem; }
  .reading-content h3 { font-size:1.2em; font-weight:600; margin:1.4rem 0 .5rem; }
  .reading-content p  { margin:0 0 1.4em; text-align:justify; hyphens:auto; }
  .reading-content blockquote { border-left:3px solid var(--rc); margin:2rem 0; padding:16px 24px; font-style:italic; opacity:.8; }
  .reading-content ul,.reading-content ol { padding-left:2em; margin:.7em 0 1.4em; }
  .reading-content li { margin-bottom:.5em; line-height:1.8; }
  .reading-content a  { color:var(--rc); }
  .reading-content code { font-size:.85em; padding:2px 6px; border-radius:4px; font-family:monospace; }
  .reading-content pre { padding:20px; border-radius:10px; overflow-x:auto; margin:1.8rem 0; font-size:14px; line-height:1.65; }
  .reading-content img { max-width:100%; border-radius:8px; margin:1rem 0; }
  .reading-content table { width:100%; border-collapse:collapse; margin:1.5rem 0; font-size:.9em; display:block; overflow-x:auto; }
  .reading-content td,.reading-content th { padding:10px 14px; border:1px solid rgba(0,0,0,.1); }
  .reading-content hr { border:none; border-top:1px solid rgba(0,0,0,.12); margin:2.5rem auto; width:40%; }
  .reading-content p:first-of-type::first-letter {
    font-size:3.2em; font-family:'Playfair Display',Georgia,serif;
    font-weight:700; float:left; line-height:.8;
    margin:.08em .1em 0 0; color:var(--rc);
  }

  /* ── Layout ── */
  .blog-grid {
    display:grid;
    grid-template-columns: minmax(0,1fr);
    gap:clamp(20px,3vw,40px);
    align-items:start;
  }
  @media (min-width:1024px) {
    .blog-grid-with-toc { grid-template-columns: minmax(0,1fr) 260px; }
  }

  /* ── Desktop TOC ── */
  .toc-desktop { display:none; }
  @media (min-width:1024px) { .toc-desktop { display:block !important; } }
  .toc-link:hover { color:#f4803e !important; }

  /* ── Header Meta Row ── */
  .post-header-outer {
    width: 100%;
    background: #f0f2f8;
    position: relative;
    z-index: 10;
  }
  .post-header-inner {
    max-width:900px;
    margin:0 auto;
    padding:clamp(16px,3vw,28px) clamp(12px,3vw,24px) 0;
    animation:fadeUp .5s ease both;
  }
  .breadcrumb-row {
    display:flex;
    align-items:center;
    gap:8px;
    margin-bottom:14px;
    flex-wrap:wrap;
  }
  .meta-row {
    display:flex;
    align-items:center;
    gap:10px;
    flex-wrap:wrap;
    margin-bottom:16px;
  }
  .meta-row-right {
    display:flex;
    align-items:center;
    gap:10px;
    flex-wrap:wrap;
    margin-left:auto;
  }
  @media (max-width:640px) {
    .meta-row { gap:8px; }
    .meta-row-right { margin-left:0; width:100%; justify-content:flex-start; }
    .meta-sep { display:none; }
  }

  /* ── Share Dropdown ── */
  .share-dropdown {
    position:absolute;
    top:calc(100% + 8px);
    right:0;
    width:220px;
    background:#fff;
    border-radius:14px;
    box-shadow:0 20px 60px rgba(0,0,0,.18);
    border:1px solid #e5e7eb;
    padding:10px;
    z-index:99999;
    animation:fadeUp .2s ease;
  }
  @media (max-width:640px) {
    .share-dropdown {
      position:fixed;
      left:12px;
      right:12px;
      bottom:80px;
      top:auto;
      width:auto;
      z-index:99999;
    }
  }

  /* ── Related Grid ── */
  .related-grid {
    display:grid;
    grid-template-columns:repeat(auto-fill, minmax(min(100%,220px),1fr));
    gap:clamp(12px,2vw,20px);
  }

  /* ── Reading Toolbar ── */
  .reading-toolbar {
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    flex-wrap:wrap;
    padding:10px clamp(12px,3vw,20px);
  }
  .reading-toolbar-controls {
    display:flex;
    align-items:center;
    gap:8px;
    flex-wrap:wrap;
  }

  /* ── Copy Blocked Toast ── */
  .copy-toast {
    position:fixed;
    bottom:clamp(20px,4vw,32px);
    left:50%;
    transform:translateX(-50%);
    background:#1a1a2e;
    color:#fff;
    padding:10px 20px;
    border-radius:30px;
    font-size:13px;
    font-weight:600;
    font-family:'DM Sans',sans-serif;
    box-shadow:0 8px 32px rgba(0,0,0,0.25);
    z-index:999999;
    pointer-events:none;
    white-space:nowrap;
    animation:fadeUp 0.25s ease;
  }

  /* ── Scrollbar ── */
  ::-webkit-scrollbar { width:5px; height:5px; }
  ::-webkit-scrollbar-thumb { background:#d1d5db; border-radius:3px; }
`;

// ─── Copy Protection Hook ─────────────────────────────────────────────────────
function useCopyProtection() {
  const [showToast, setShowToast] = useState(false);
  const toastTimer = useRef(null);

  useEffect(() => {
    const triggerToast = () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
      setShowToast(true);
      toastTimer.current = setTimeout(() => setShowToast(false), 2000);
    };

    const blockCopy   = (e) => { e.preventDefault(); triggerToast(); return false; };
    const blockCut    = (e) => { e.preventDefault(); triggerToast(); return false; };
    const blockSelect = (e) => { e.preventDefault(); return false; };
    const blockContext = (e) => { e.preventDefault(); triggerToast(); return false; };
    const blockKeyboard = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        ["c","x","a","u","s","p"].includes(e.key.toLowerCase())
      ) {
        e.preventDefault();
        triggerToast();
        return false;
      }
      if (e.key === "F12" || (e.ctrlKey && e.shiftKey && ["i","j","c"].includes(e.key.toLowerCase()))) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("copy",        blockCopy,      { passive: false });
    document.addEventListener("cut",         blockCut,       { passive: false });
    document.addEventListener("selectstart", blockSelect,    { passive: false });
    document.addEventListener("contextmenu", blockContext,   { passive: false });
    document.addEventListener("keydown",     blockKeyboard,  { passive: false });

    return () => {
      document.removeEventListener("copy",        blockCopy);
      document.removeEventListener("cut",         blockCut);
      document.removeEventListener("selectstart", blockSelect);
      document.removeEventListener("contextmenu", blockContext);
      document.removeEventListener("keydown",     blockKeyboard);
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  return showToast;
}

// ─── Copy Toast ───────────────────────────────────────────────────────────────
function CopyToast({ show }) {
  if (!show) return null;
  return (
    <div className="copy-toast">
      🔒 Content is protected — copying is disabled
    </div>
  );
}

// ─── Reading Progress Bar ─────────────────────────────────────────────────────
function ReadingProgress({ color }) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const handler = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? (el.scrollTop / total) * 100 : 0);
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, height: "3px",
      background: "rgba(0,0,0,0.08)", zIndex: 99999,
    }}>
      <div style={{
        height: "100%",
        background: `linear-gradient(90deg, ${color || "#f4803e"}, #4361ee)`,
        width: `${pct}%`, transition: "width 0.1s linear",
        boxShadow: `0 0 8px rgba(244,128,62,0.6)`,
      }} />
    </div>
  );
}

// ─── Related Card ─────────────────────────────────────────────────────────────
function RelatedCard({ blog, onClick }) {
  const [hovered, setHovered] = useState(false);
  const color = categoryColor(blog.category);
  return (
    <div
      onClick={() => onClick(blog.slug || blog.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff", borderRadius: "14px", overflow: "hidden",
        cursor: "pointer",
        boxShadow: hovered ? "0 12px 40px rgba(0,0,0,0.12)" : "0 2px 12px rgba(0,0,0,0.06)",
        transform: hovered ? "translateY(-4px)" : "none",
        transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)",
        display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ height: "130px", overflow: "hidden", background: "#f0f2f8", flexShrink: 0 }}>
        {blog.featuredImg
          ? <img src={blog.featuredImg} alt={blog.title} style={{ width: "100%", height: "100%", objectFit: "cover", transform: hovered ? "scale(1.06)" : "scale(1)", transition: "transform 0.5s ease" }} />
          : <div style={{ width: "100%", height: "100%", background: `${color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BookOpen size={26} color={color} style={{ opacity: 0.4 }} />
            </div>
        }
      </div>
      <div style={{ padding: "14px", flex: 1 }}>
        <div style={{ fontSize: "10px", fontWeight: "700", color, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "5px" }}>{blog.category}</div>
        <h4 style={{
          fontSize: "13px", fontWeight: "700", color: "#1a1a2e", lineHeight: "1.4",
          fontFamily: "'Playfair Display', serif",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden", margin: 0,
        }}>{blog.title}</h4>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", fontSize: "11px", color: "#9ca3af" }}>
          <Clock size={10} /> {blog.readTime || 1} min · <Eye size={10} /> {blog.views || 0}
        </div>
      </div>
    </div>
  );
}

// ─── Mobile TOC Drawer ────────────────────────────────────────────────────────
function MobileTOC({ headings, activeId, onSelect, color }) {
  const [open, setOpen] = useState(false);
  if (headings.length <= 2) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          position: "fixed", bottom: "80px", right: "16px",
          background: color, color: "#fff", border: "none",
          borderRadius: "50px", padding: "10px 16px",
          display: "flex", alignItems: "center", gap: "6px",
          fontSize: "13px", fontWeight: "700", cursor: "pointer",
          boxShadow: `0 4px 20px ${color}50`, zIndex: 500,
        }}
      >
        <AlignJustify size={15} /> Contents
      </button>
      {open && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9000,
            background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "flex-end",
          }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: "100%", background: "#fff",
              borderRadius: "24px 24px 0 0",
              padding: "24px 20px 40px",
              maxHeight: "72vh", overflowY: "auto",
              animation: "slideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <span style={{ fontSize: "14px", fontWeight: "700", color: "#1a1a2e", display: "flex", alignItems: "center", gap: "6px" }}>
                <BookOpen size={14} color={color} /> In This Article
              </span>
              <button onClick={() => setOpen(false)} style={{ background: "#f0f2f8", border: "none", borderRadius: "50%", width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <X size={14} />
              </button>
            </div>
            {headings.map(h => (
              <button
                key={h.id}
                onClick={() => { onSelect(h.id); setOpen(false); }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  background: activeId === h.id ? `${color}10` : "transparent",
                  color: activeId === h.id ? color : "#374151",
                  border: "none", cursor: "pointer",
                  padding: `10px 10px 10px ${h.tag === "H1" ? "10px" : h.tag === "H2" ? "20px" : "30px"}`,
                  borderRadius: "8px", fontSize: "13px", lineHeight: "1.5",
                  borderLeft: activeId === h.id ? `3px solid ${color}` : "3px solid transparent",
                  marginBottom: "2px",
                }}
              >
                {h.text}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ─── View Mode Toggle ─────────────────────────────────────────────────────────
function ViewToggle({ mode, onChange }) {
  return (
    <div style={{
      display: "inline-flex", background: "#f0f2f8",
      borderRadius: "10px", padding: "3px", gap: "2px",
    }}>
      {[
        { id: "web",     icon: <Monitor size={13} />,   label: "Web" },
        { id: "reading", icon: <BookMarked size={13} />, label: "Reading" },
      ].map(v => (
        <button
          key={v.id}
          onClick={() => onChange(v.id)}
          style={{
            display: "flex", alignItems: "center", gap: "5px",
            padding: "6px 10px", border: "none", borderRadius: "7px",
            fontSize: "12px", fontWeight: "600", cursor: "pointer",
            background: mode === v.id ? "#fff" : "transparent",
            color: mode === v.id ? "#1a1a2e" : "#9ca3af",
            boxShadow: mode === v.id ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
            transition: "all 0.2s", whiteSpace: "nowrap",
          }}
        >
          {v.icon} {v.label}
        </button>
      ))}
    </div>
  );
}

// ─── Reading View ─────────────────────────────────────────────────────────────
function ReadingView({ blog, color, onClose }) {
  const [fontSize, setFontSize] = useState(18);
  const [theme, setTheme] = useState("paper");

  const themes = {
    paper: { bg: "#f5f0e8", text: "#2c1810", card: "#faf6ef", border: "#d4c4a8" },
    white: { bg: "#ffffff", text: "#1a1a2e", card: "#ffffff", border: "#e5e7eb" },
    dark:  { bg: "#1a1a2e", text: "#e8e0d0", card: "#242438", border: "#333355" },
    sepia: { bg: "#f7f3eb", text: "#433422", card: "#f0ebe0", border: "#c9b896" },
  };
  const t = themes[theme];

  return (
    <div
      className="no-select"
      style={{
        position: "fixed", inset: 0, zIndex: 800,
        background: t.bg, overflowY: "auto",
        transition: "background 0.3s",
        fontFamily: "'Lora','Georgia',serif",
      }}
    >
      <style>{`:root { --rc: ${color}; }`}</style>

      {/* Toolbar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 810,
        background: t.card, borderBottom: `1px solid ${t.border}`,
        backdropFilter: "blur(8px)",
      }}>
        <div className="reading-toolbar">
          {/* Left: logo + label */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <img
              src={logo} alt="logo"
              style={{ height: "24px", objectFit: "contain", cursor: "pointer" }}
              onClick={() => window.location.href = "/"}
            />
            <BookMarked size={15} color={color} />
            <span style={{ fontSize: "11px", fontWeight: "700", color: t.text, opacity: 0.6, letterSpacing: "0.5px" }}>
              READING VIEW
            </span>
          </div>

          {/* Right: controls */}
          <div className="reading-toolbar-controls">
            {/* Font size */}
            <div style={{
              display: "flex", alignItems: "center", gap: "2px",
              background: t.bg, borderRadius: "8px", padding: "3px",
            }}>
              <button
                onClick={() => setFontSize(f => Math.max(14, f - 1))}
                style={{ width: "28px", height: "28px", border: "none", background: "transparent", cursor: "pointer", fontSize: "13px", color: t.text, borderRadius: "5px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >A−</button>
              <span style={{ fontSize: "11px", color: t.text, opacity: 0.5, minWidth: "22px", textAlign: "center" }}>{fontSize}</span>
              <button
                onClick={() => setFontSize(f => Math.min(28, f + 1))}
                style={{ width: "28px", height: "28px", border: "none", background: "transparent", cursor: "pointer", fontSize: "15px", color: t.text, borderRadius: "5px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >A+</button>
            </div>

            {/* Theme swatches */}
            <div style={{ display: "flex", gap: "4px" }}>
              {Object.entries(themes).map(([key, val]) => (
                <button
                  key={key} onClick={() => setTheme(key)} title={key}
                  style={{
                    width: "20px", height: "20px", borderRadius: "50%",
                    background: val.bg,
                    border: theme === key ? `2px solid ${color}` : `2px solid ${val.border}`,
                    cursor: "pointer",
                    transform: theme === key ? "scale(1.2)" : "scale(1)",
                    transition: "transform 0.2s",
                  }}
                />
              ))}
            </div>

            {/* Back to web */}
            <button
              onClick={onClose}
              style={{
                display: "flex", alignItems: "center", gap: "5px",
                background: color, color: "#fff", border: "none",
                borderRadius: "8px", padding: "6px 12px",
                fontSize: "12px", fontWeight: "700", cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <Monitor size={12} /> Web View
            </button>
          </div>
        </div>
      </div>

      {/* Book content */}
      <div style={{
        maxWidth: "680px", margin: "0 auto",
        padding: "clamp(24px,5vw,60px) clamp(16px,5vw,48px)",
      }}>
        {/* Chapter header */}
        <div style={{
          textAlign: "center", marginBottom: "48px",
          paddingBottom: "32px", borderBottom: `2px solid ${t.border}`,
        }}>
          <span style={{
            display: "inline-block", background: `${color}20`, color,
            padding: "4px 14px", borderRadius: "20px",
            fontSize: "11px", fontWeight: "700", textTransform: "uppercase",
            letterSpacing: "1px", marginBottom: "20px",
          }}>{blog.category}</span>

          <h1 style={{
            fontSize: "clamp(20px,4vw,38px)", fontWeight: "700",
            color: t.text, lineHeight: "1.25",
            fontFamily: "'Playfair Display','Georgia',serif",
            marginBottom: "16px",
          }}>{blog.title}</h1>

          {blog.excerpt && (
            <p style={{
              fontSize: "clamp(14px,1.8vw,18px)", color: t.text, opacity: 0.65,
              fontStyle: "italic", lineHeight: "1.7",
              maxWidth: "560px", margin: "0 auto 20px",
            }}>{blog.excerpt}</p>
          )}

          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "16px", flexWrap: "wrap", fontSize: "12px", color: t.text, opacity: 0.5,
          }}>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Calendar size={12} /> {fmtDate(blog.publishedAt)}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Clock size={12} /> {blog.readTime || 1} min read
            </span>
            <span style={{ fontStyle: "italic" }}>— {blog.author || "Admin"}</span>
          </div>
        </div>

        {/* Ornament */}
        <div style={{ textAlign: "center", marginBottom: "40px", color: t.text, opacity: 0.25, fontSize: "18px", letterSpacing: "10px" }}>❧ ✦ ❧</div>

        {/* Body */}
        <div
          className="reading-content no-select"
          style={{ fontSize: `${fontSize}px`, lineHeight: "1.9", color: t.text }}
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* End ornament */}
        <div style={{ textAlign: "center", margin: "48px 0 24px", color: t.text, opacity: 0.25, fontSize: "18px", letterSpacing: "10px" }}>✦ ✦ ✦</div>

        {/* Tags */}
        {blog.tags?.length > 0 && (
          <div style={{
            display: "flex", flexWrap: "wrap", gap: "8px",
            justifyContent: "center",
            paddingTop: "24px", borderTop: `1px solid ${t.border}`,
          }}>
            {blog.tags.map(tag => (
              <span key={tag} style={{
                background: `${color}15`, color,
                padding: "4px 12px", borderRadius: "20px",
                fontSize: "12px", fontWeight: "600",
              }}>#{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Share Button ─────────────────────────────────────────────────────────────
function ShareButton({ blog }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied]     = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!shareOpen) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShareOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [shareOpen]);

  const handleShare = () => {
    if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      navigator.share({ title: blog.title, text: blog.excerpt || blog.title, url: window.location.href });
    } else {
      setShareOpen(p => !p);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const SHARE_OPTIONS = [
    { label: "WhatsApp",    color: "#25D366", link: () => `https://wa.me/?text=${encodeURIComponent(blog.title + " " + window.location.href)}` },
    { label: "X (Twitter)", color: "#000",    link: () => `https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}` },
    { label: "Facebook",    color: "#1877F2", link: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
    { label: "LinkedIn",    color: "#0077B5", link: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}` },
    { label: "Telegram",    color: "#0088cc", link: () => `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}` },
  ];

  return (
    <div ref={ref} style={{ position: "relative", zIndex: 99999 }}>
      <button
        onClick={handleShare}
        style={{
          display: "flex", alignItems: "center", gap: "5px",
          background: "#fff", border: "1.5px solid #e5e7eb",
          borderRadius: "8px", padding: "6px 12px",
          cursor: "pointer", fontSize: "12px", fontWeight: "600", color: "#374151",
          whiteSpace: "nowrap",
        }}
      >
        <Share2 size={13} /> Share
      </button>

      {shareOpen && (
        <div className="share-dropdown">
          {SHARE_OPTIONS.map(s => (
            <a
              key={s.label}
              href={s.link()}
              target="_blank" rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", gap: "10px",
                padding: "9px 10px", borderRadius: "10px",
                fontSize: "13px", fontWeight: "600",
                color: s.color, textDecoration: "none", transition: ".15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {s.label}
            </a>
          ))}
          <button
            onClick={handleCopy}
            style={{
              marginTop: "8px", width: "100%",
              background: "#f3f4f6", border: "none",
              padding: "9px", borderRadius: "10px",
              fontWeight: "600", cursor: "pointer", fontSize: "13px",
            }}
          >
            {copied ? "✅ Link Copied" : "🔗 Copy Article Link"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────
export default function BlogPostPage() {
  const { slug: rawSlug } = useParams();
  const slug = decodeURIComponent(rawSlug)
    .replace(/—/g, "-").replace(/–/g, "-")
    .replace(/\s+/g, "-").toLowerCase();

  const navigate   = useNavigate();
  const contentRef = useRef(null);

  const [blog,     setBlog]     = useState(null);
  const [related,  setRelated]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [viewMode, setViewMode] = useState("web");

  // Copy protection
  const showCopyToast = useCopyProtection();

  // Fetch blog
  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true); setError(null);
    getBlogBySlug(slug)
      .then((json) => {
        if (!json?.data) throw new Error("Blog not found");
        setBlog(json.data.blog || json.data);
        setRelated(json.data.related || []);
      })
      .catch((e) => setError(e.message || "Article not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  // Extract headings for TOC
  useEffect(() => {
    if (!blog || !contentRef.current) return;
    const els = contentRef.current.querySelectorAll("h1, h2, h3");
    const h = [];
    els.forEach((el, i) => {
      const id = `heading-${i}`;
      el.id = id;
      h.push({ id, text: el.innerText, tag: el.tagName });
    });
    setHeadings(h);
  }, [blog]);

  // Scroll-spy
  useEffect(() => {
    if (!headings.length) return;
    const handler = () => {
      let current = "";
      headings.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 120) current = id;
      });
      setActiveId(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [headings]);

  const scrollToHeading = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleRelatedClick = (s) => { navigate(`/blog/${s}`); window.scrollTo(0, 0); };
  const color = blog ? categoryColor(blog.category) : "#f4803e";

  // ── Loading ──
  if (loading) return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f2f8" }}>
        <div style={{ textAlign: "center" }}>
          <Loader2 size={36} color="#f4803e" style={{ animation: "spin 0.9s linear infinite", marginBottom: "12px" }} />
          <p style={{ color: "#6b7280", fontSize: "14px" }}>Loading article…</p>
        </div>
      </div>
    </>
  );

  // ── Error ──
  if (error) return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f2f8", padding: "20px" }}>
        <div style={{
          textAlign: "center", background: "#fff", borderRadius: "16px",
          padding: "clamp(28px,5vw,48px) clamp(20px,5vw,32px)",
          maxWidth: "400px", width: "100%",
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>😞</div>
          <h2 style={{ fontSize: "clamp(16px,4vw,20px)", fontWeight: "700", color: "#1a1a2e", marginBottom: "8px" }}>Article Not Found</h2>
          <p style={{ color: "#6b7280", fontSize: "14px", marginBottom: "20px" }}>{error}</p>
          <button
            onClick={() => navigate("/blog")}
            style={{ background: "#f4803e", color: "#fff", border: "none", borderRadius: "8px", padding: "10px 24px", cursor: "pointer", fontSize: "14px", fontWeight: "600" }}
          >
            ← Back to Blog
          </button>
        </div>
      </div>
    </>
  );

  // ── Reading View ──
  if (viewMode === "reading") {
    return (
      <>
        <style>{GLOBAL_STYLES}</style>
        <ReadingProgress color={color} />
        <ReadingView blog={blog} color={color} onClose={() => setViewMode("web")} />
        <CopyToast show={showCopyToast} />
      </>
    );
  }

  const hasToc = headings.length > 2;

  // ── Web View ──
  return (
    <div
      className="no-select"
      style={{
        fontFamily: "'DM Sans', Arial, sans-serif",
        background: "#f0f2f8",
        minHeight: "100vh",
        color: "#1a1a2e",
        paddingTop: "clamp(60px,10vw,80px)",
        overflowX: "hidden",
      }}
    >
      <style>{GLOBAL_STYLES}</style>
      <ReadingProgress color={color} />
      <CopyToast show={showCopyToast} />

      {/* ── Page Header ── */}
      <div className="post-header-outer">
        <div className="post-header-inner">
          {/* Breadcrumb */}
          <div className="breadcrumb-row">
            <button
              onClick={() => navigate("/blog")}
              style={{
                display: "flex", alignItems: "center", gap: "5px",
                background: "#fff", border: "1.5px solid #e5e7eb",
                borderRadius: "8px", padding: "6px 12px",
                cursor: "pointer", fontSize: "13px", fontWeight: "600", color: "#374151",
                flexShrink: 0,
              }}
            >
              <ArrowLeft size={14} /> Blog
            </button>
            <ChevronRight size={14} color="#9ca3af" />
            <span style={{
              fontSize: "13px", color,
              background: `${color}10`,
              padding: "3px 10px", borderRadius: "6px",
              fontWeight: "600", maxWidth: "200px",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{blog.category}</span>
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(20px,5vw,38px)",
            fontWeight: "700", color: "#1a1a2e", lineHeight: "1.25",
            marginBottom: "14px", margin: "0 0 14px",
          }}>{blog.title}</h1>

          {/* Excerpt */}
          {blog.excerpt && (
            <p style={{
              fontSize: "clamp(14px,2vw,17px)", color: "#6b7280",
              lineHeight: "1.7", marginBottom: "14px",
              maxWidth: "720px",
            }}>{blog.excerpt}</p>
          )}

          {/* Meta Row */}
          <div className="meta-row">
            {/* Left: date + author + words */}
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#9ca3af", flexShrink: 0 }}>
              <Calendar size={12} color={color} />
              <span style={{ color: "#374151", fontWeight: "500" }}>{fmtDate(blog.publishedAt)}</span>
            </span>

            <span className="meta-sep" style={{ color: "#e5e7eb", fontSize: "14px" }}>|</span>

            <span style={{ fontSize: "12px", color: "#9ca3af", flexShrink: 0 }}>
              by <strong style={{ color: "#374151" }}>{blog.author || "Admin"}</strong>
            </span>

            <span className="meta-sep" style={{ color: "#e5e7eb", fontSize: "14px" }}>|</span>

            <span style={{ fontSize: "12px", color: "#9ca3af", flexShrink: 0 }}>
              {blog.wordCount || 0} words
            </span>

            {/* Right: stats + view toggle + share */}
            <div className="meta-row-right">
              <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#9ca3af", whiteSpace: "nowrap" }}>
                <Clock size={12} color={color} /> {blog.readTime || 1} min
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#9ca3af", whiteSpace: "nowrap" }}>
                <Eye size={12} color={color} /> {blog.views || 0}
              </span>
              <ViewToggle mode={viewMode} onChange={setViewMode} />
              <ShareButton blog={blog} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Featured Image ── */}
      {blog.featuredImg && (
        <div style={{ maxWidth: "900px", margin: "16px auto 0", padding: "0 clamp(12px,3vw,24px)" }}>
          <img
            src={blog.featuredImg} alt={blog.title}
            style={{
              width: "100%",
              maxHeight: "clamp(200px,40vw,460px)",
              objectFit: "cover",
              borderRadius: "clamp(10px,2vw,16px)",
              boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
              animation: "fadeUp 0.5s ease 0.25s both",
              display: "block",
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {/* ── Body ── */}
      <div
        className={`blog-grid ${hasToc ? "blog-grid-with-toc" : ""}`}
        style={{
          maxWidth: "1200px", margin: "0 auto",
          padding: "clamp(16px,4vw,28px) clamp(12px,3vw,32px) clamp(40px,8vw,64px)",
        }}
      >
        {/* Article */}
        <article style={{ minWidth: 0 }}>
          <div
            ref={contentRef}
            className="post-content no-select"
            style={{
              background: "#fff", borderRadius: "clamp(10px,2vw,16px)",
              padding: "clamp(16px,5vw,44px)",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              fontSize: "clamp(15px,2vw,17px)", lineHeight: "1.85", color: "#374151",
              animation: "fadeUp 0.5s ease 0.3s both",
            }}
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* Tags */}
          {blog.tags?.length > 0 && (
            <div style={{
              background: "#fff", borderRadius: "12px",
              padding: "clamp(12px,3vw,18px)",
              marginTop: "14px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px",
            }}>
              <TagIcon size={14} color="#9ca3af" />
              {blog.tags.map(t => (
                <Link
                  key={t}
                  to={`/blog?tag=${encodeURIComponent(t)}`}
                  style={{
                    background: `${color}10`, color,
                    padding: "4px 12px", borderRadius: "20px",
                    fontSize: "12px", fontWeight: "600", textDecoration: "none",
                  }}
                >
                  #{t}
                </Link>
              ))}
            </div>
          )}

          {/* Author card */}
          <div style={{
            background: `linear-gradient(135deg, ${color}08 0%, #fff 100%)`,
            border: `1.5px solid ${color}20`,
            borderRadius: "clamp(10px,2vw,16px)",
            padding: "clamp(14px,3vw,22px)",
            marginTop: "14px",
            display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}>
            <div style={{
              width: "48px", height: "48px", borderRadius: "50%", flexShrink: 0,
              background: `linear-gradient(135deg, ${color} 0%, ${color}88 100%)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "18px", fontWeight: "700", color: "#fff",
            }}>
              {(blog.author || "A")[0].toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#1a1a2e", marginBottom: "3px" }}>
                Written by {blog.author || "Admin"}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                Published {fmtDate(blog.publishedAt)} · {blog.wordCount || 0} words
              </div>
            </div>
          </div>
        </article>

        {/* Desktop TOC */}
        {hasToc && (
          <aside
            className="toc-desktop"
            style={{
              position: "sticky", top: "100px",
              background: "#fff", borderRadius: "14px", padding: "18px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              maxHeight: "calc(100vh - 140px)", overflowY: "auto",
              zIndex: 5,
            }}
          >
            <div style={{
              fontSize: "11px", fontWeight: "700", color: "#374151",
              textTransform: "uppercase", letterSpacing: "0.8px",
              marginBottom: "12px",
              display: "flex", alignItems: "center", gap: "6px",
            }}>
              <BookOpen size={12} color={color} /> In This Article
            </div>
            {headings.map(h => (
              <button
                key={h.id}
                className="toc-link"
                onClick={() => scrollToHeading(h.id)}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  background: activeId === h.id ? `${color}10` : "transparent",
                  color: activeId === h.id ? color : "#6b7280",
                  border: "none", cursor: "pointer",
                  padding: `5px 8px 5px ${h.tag === "H1" ? "8px" : h.tag === "H2" ? "16px" : "24px"}`,
                  borderRadius: "6px", fontSize: "12px", lineHeight: "1.5",
                  borderLeft: activeId === h.id ? `2px solid ${color}` : "2px solid transparent",
                  transition: "all 0.2s",
                }}
              >
                {h.text}
              </button>
            ))}
          </aside>
        )}
      </div>

      {/* ── Related Articles ── */}
      {related.length > 0 && (
        <div style={{
          maxWidth: "1200px", margin: "0 auto",
          padding: "0 clamp(12px,3vw,32px) clamp(40px,8vw,64px)",
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: "14px", flexWrap: "wrap", gap: "8px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <TrendingUp size={15} color="#f4803e" />
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#374151", textTransform: "uppercase", letterSpacing: "0.7px" }}>
                Related Articles
              </span>
            </div>
            <Link
              to="/blog"
              style={{ display: "flex", alignItems: "center", gap: "4px", color: "#f4803e", textDecoration: "none", fontSize: "13px", fontWeight: "600" }}
            >
              View All <ChevronRight size={14} />
            </Link>
          </div>
          <div className="related-grid">
            {related.map(b => <RelatedCard key={b.id} blog={b} onClick={handleRelatedClick} />)}
          </div>
        </div>
      )}

      {/* Mobile TOC FAB */}
      <MobileTOC headings={headings} activeId={activeId} onSelect={scrollToHeading} color={color} />
    </div>
  );
}